// Shared UI primitives — LŪMEN dark design system: black canvas, white ink,
// #AFDDFF accent, accessible and keyboard-friendly.
import React from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends React.ComponentProps<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-[#AFDDFF] text-black hover:bg-[#c8e8ff] focus-visible:ring-[#AFDDFF] active:scale-[0.98] disabled:bg-white/30",
  secondary:
    "bg-white/[0.08] text-white border border-white/20 hover:border-white/40 hover:bg-white/[0.12] focus-visible:ring-[#AFDDFF] active:scale-[0.98]",
  ghost:
    "bg-transparent text-white hover:bg-white/10 focus-visible:ring-[#AFDDFF] active:scale-[0.98]",
  danger:
    "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-400 active:scale-[0.98] disabled:bg-red-900",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-5 py-2.5 text-sm",
  lg: "px-7 py-3.5 text-base",
};

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  children,
  className = "",
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 font-manrope font-medium transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:cursor-not-allowed disabled:opacity-60 ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
}

export function Card({ children, className = "", ...rest }: React.ComponentProps<"div">) {
  return (
    <div
      {...rest}
      className={`border border-white/10 bg-white/[0.04] shadow-[0_8px_30px_rgba(0,0,0,0.35)] ${className}`}
    >
      {children}
    </div>
  );
}

export function GlassCard({ children, className = "", ...rest }: React.ComponentProps<"div">) {
  return (
    <div
      {...rest}
      className={`border-2 border-white/20 bg-white/[0.06] shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-2xl ${className}`}
    >
      {children}
    </div>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  align = "center",
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: "center" | "left";
}) {
  const alignClass = align === "center" ? "text-center items-center" : "text-left items-start";
  return (
    <div className={`flex flex-col gap-3 ${alignClass}`}>
      {eyebrow && (
        <span className="font-manrope text-xs font-medium uppercase tracking-[0.22em] text-[#AFDDFF]">
          [ {eyebrow} ]
        </span>
      )}
      <h2 className="max-w-3xl font-graphik text-3xl font-normal leading-tight text-white md:text-4xl">
        {title}
      </h2>
      {description && (
        <p className="max-w-2xl text-base leading-relaxed text-white/50">{description}</p>
      )}
    </div>
  );
}

export function Badge({
  children,
  tone = "neutral",
  className = "",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "accent" | "success" | "warning" | "danger" | "dark";
  className?: string;
}) {
  const tones: Record<string, string> = {
    neutral: "bg-white/[0.08] text-white/80",
    accent: "bg-[#AFDDFF]/15 text-[#AFDDFF]",
    success: "bg-emerald-500/15 text-emerald-300",
    warning: "bg-amber-500/15 text-amber-300",
    danger: "bg-red-500/15 text-red-300",
    dark: "bg-[#AFDDFF] text-black",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="font-manrope text-sm font-medium text-white/85">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-white/50">{hint}</p>}
      {error && <p className="text-xs font-medium text-red-400">{error}</p>}
    </div>
  );
}

const inputClasses =
  "w-full border border-white/15 bg-white/[0.06] px-4 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-white/40 focus:border-[#AFDDFF] focus:ring-2 focus:ring-[#AFDDFF]/25 disabled:bg-white/[0.03]";

export function Input(props: React.ComponentProps<"input">) {
  return <input {...props} className={`${inputClasses} ${props.className ?? ""}`} />;
}

export function Select(props: React.ComponentProps<"select">) {
  return <select {...props} className={`${inputClasses} ${props.className ?? ""}`} />;
}

export function Textarea(props: React.ComponentProps<"textarea">) {
  return <textarea {...props} className={`${inputClasses} min-h-[96px] ${props.className ?? ""}`} />;
}

export function Checkbox({
  label,
  ...rest
}: Omit<React.ComponentProps<"input">, "type"> & { label: React.ReactNode }) {
  return (
    <label className="flex items-start gap-2.5 text-sm text-white/85">
      <input
        type="checkbox"
        {...rest}
        className="mt-0.5 h-4 w-4 rounded-none border-white/25 accent-[#AFDDFF] focus-visible:ring-2 focus-visible:ring-[#AFDDFF]/40"
      />
      <span>{label}</span>
    </label>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-10 text-white/50" role="status">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#AFDDFF] border-t-transparent" />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function EmptyState({ title, body }: { title: string; body?: string }) {
  return (
    <div className="border border-dashed border-white/15 bg-white/[0.02] px-6 py-10 text-center">
      <p className="text-sm font-medium text-white/85">{title}</p>
      {body && <p className="mt-1 text-sm text-white/50">{body}</p>}
    </div>
  );
}

export function Notice({
  tone = "info",
  children,
}: {
  tone?: "info" | "success" | "warning" | "error";
  children: React.ReactNode;
}) {
  const tones: Record<string, string> = {
    info: "border-sky-400/30 bg-sky-400/10 text-sky-200",
    success: "border-emerald-400/30 bg-emerald-400/10 text-emerald-200",
    warning: "border-amber-400/30 bg-amber-400/10 text-amber-200",
    error: "border-red-400/30 bg-red-400/10 text-red-200",
  };
  return (
    <div className={`border px-4 py-3 text-sm ${tones[tone]}`} role="note">
      {children}
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="mt-10 w-full max-w-2xl border border-white/10 bg-[#0c0c0c] p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h3 className="text-lg font-medium text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-[#AFDDFF]"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
